import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import { useGoBack } from "../../../hooks/useGoBack";
import { listCampaignsApi, type CampaignListItem } from "../campanhasApi";
import {
  cleanContent,
  createCreativeApi,
  EMPTY_CONTENT,
  suggestContentApi,
  type CreativeContent,
  type CreativeFormat,
} from "../criativosApi";
import { CreativeContentForm, GeneratingNotice } from "../components/CreativeParts";
import { CAMPANHAS_PAGE, CRIATIVO_PAGE, CRIATIVOS_PAGE } from "../routes";
import "./Campanhas.css";
import "./Criativos.css";

const FORMATS: { value: CreativeFormat; label: string }[] = [
  { value: "FEED", label: "Feed (4:5)" },
  { value: "STORY", label: "Stories (9:16)" },
];

/** O que falta para gerar, igual à regra do servidor. */
function missing(c: CreativeContent, photo: string): string | null {
  const x = cleanContent(c);
  if (!x.roleTitle) return "Informe o cargo.";
  if (!x.benefits.length) return "Informe pelo menos um benefício.";
  if (!x.requirements.length) return "Informe pelo menos um requisito.";
  if (!x.schedule) return "Informe a escala de trabalho.";
  if (!x.locationLine1) return "Informe o local de trabalho.";
  if (!photo.trim()) return "Descreva a foto.";
  return null;
}

/** Novo criativo: a Eco-IA escreve os textos, a pessoa confere e o Google desenha. */
const CriativoNovoPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const goBack = useGoBack(CRIATIVOS_PAGE());
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [campaignId, setCampaignId] = useState(params.get("campanha") ?? "");
  const [brief, setBrief] = useState("");
  const [content, setContent] = useState<CreativeContent>(EMPTY_CONTENT);
  const [photoPrompt, setPhotoPrompt] = useState("");
  const [format, setFormat] = useState<CreativeFormat>("FEED");
  const [explanation, setExplanation] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    listCampaignsApi()
      .then((list) => setCampaigns(list.filter((c) => c.status !== "ENCERRADA")))
      .catch(() => undefined);
  }, []);

  const write = async () => {
    if (!campaignId && !brief.trim()) return toast.error("Escolha uma campanha ou descreva a vaga.");
    try {
      setWriting(true);
      const s = await suggestContentApi({ campaignId: campaignId || undefined, brief: brief.trim() || undefined });
      setContent({
        ...s.content,
        benefits: s.content.benefits.length ? s.content.benefits : EMPTY_CONTENT.benefits,
        requirements: s.content.requirements.length ? s.content.requirements : EMPTY_CONTENT.requirements,
      });
      setPhotoPrompt(s.photoPrompt);
      setExplanation(s.explanation);
    } catch (e: any) {
      toast.error(e?.message);
    } finally {
      setWriting(false);
    }
  };

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = missing(content, photoPrompt);
    if (problem) return toast.error(problem);
    try {
      setGenerating(true);
      const out = await createCreativeApi({ campaignId: campaignId || undefined, content: cleanContent(content), photoPrompt: photoPrompt.trim(), format });
      toast.success(out.review?.approved ? "Criativo pronto e conferido pela Eco-IA." : "Criativo pronto. Veja o que a Eco-IA apontou.");
      navigate(CRIATIVO_PAGE(out.id), { replace: true });
    } catch (err: any) {
      toast.error(err?.message);
      setGenerating(false);
    }
  };

  const busy = writing || generating;

  return (
    <div className="cmpPage">
      <main className="cmpPage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate("/") },
            { label: "Campanhas", onClick: () => navigate(CAMPANHAS_PAGE()) },
            { label: "Criativos", onClick: goBack },
            { label: "Novo" },
          ]}
          title="Novo criativo"
          subtitle="1. A Eco-IA escreve os textos a partir da campanha. 2. Você confere e ajusta. 3. A arte é desenhada no padrão da empresa e conferida."
        />

        <section className="ecoCard cmpPanel">
          <h2 className="cmpPanel__title">1. Sobre a vaga</h2>
          <p className="cmpHint">Escolha a campanha e/ou escreva as informações da vaga. Depois clique em "Escrever os textos com a Eco-IA": ela monta os textos da arte, e você confere no passo 2.</p>
          <div className="cmpGrid">
            <label className="ecoField">
              <span className="ecoLabel">
                Campanha <span className="ecoLabel__optional">(opcional)</span>
              </span>
              <select className="ecoInput" value={campaignId} disabled={busy} onChange={(e) => setCampaignId(e.target.value)}>
                <option value="">Sem campanha</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="ecoField">
              <span className="ecoLabel">
                Informações da vaga para a arte
                {campaignId ? <span className="ecoLabel__optional"> (opcional)</span> : null}
              </span>
              <textarea
                className="ecoInput cmpTextarea"
                rows={4}
                maxLength={1500}
                disabled={busy}
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="Ex.: Vaga de entregador de moto. Diária de R$ 120, pagamento semanal, lanche no turno. Precisa de moto própria e CNH A. Fins de semana e feriados, nas lojas da Zona Sul do Rio."
              />
              <span className="cmpHint">
                {campaignId
                  ? "A Eco-IA já lê a vaga e os locais da campanha. Escreva aqui só o que a campanha não diz e você quer na arte: valor da diária ou do salário, benefícios, requisitos, escala. Sem essas informações, a arte sai sem valores."
                  : "Sem campanha, escreva tudo o que a arte deve mostrar: o cargo, o valor da diária ou do salário, os benefícios, os requisitos, a escala e o local. A Eco-IA só usa o que estiver escrito aqui; ela não inventa valores."}
              </span>
            </label>
          </div>
          <div className="cmpActions">
            <button type="button" className="ecoBtn ecoBtn--primary" onClick={write} disabled={busy}>
              <EcoIcon name="helpCircle" size={18} />
              {writing ? "A Eco-IA está escrevendo..." : "Escrever os textos com a Eco-IA"}
            </button>
          </div>
          {explanation ? (
            <p className="cmpNotice cmpNotice--info">
              <EcoIcon name="helpCircle" size={16} />
              <span>{explanation}</span>
            </p>
          ) : null}
        </section>

        <form className="cmpFormGrid" onSubmit={generate}>
          <section className="ecoCard cmpPanel">
            <h2 className="cmpPanel__title">2. Textos da arte</h2>
            <p className="cmpHint">A arte mostra exatamente o que estiver aqui. Pode preencher à mão, sem a Eco-IA.</p>
            <CreativeContentForm value={content} onChange={setContent} disabled={busy} />
          </section>

          <section className="ecoCard cmpPanel">
            <h2 className="cmpPanel__title">3. Foto e formato</h2>
            <label className="ecoField">
              <span className="ecoLabel">Como é a foto</span>
              <textarea
                className="ecoInput cmpTextarea"
                rows={3}
                maxLength={600}
                disabled={busy}
                value={photoPrompt}
                onChange={(e) => setPhotoPrompt(e.target.value)}
                placeholder="Ex.: Entregadora sorrindo, de capacete aberto, ao lado de uma moto com baú, numa rua arborizada no fim da tarde."
              />
              <span className="cmpHint">A pessoa sempre veste a camisa da Eco. Sem marca de outra empresa.</span>
            </label>
            <EcoSegmented label="Formato" options={FORMATS} value={format} onChange={setFormat} />
            {generating ? <GeneratingNotice label="Gerando o criativo" /> : null}
            <div className="cmpFooter">
              <button type="button" className="ecoBtn" onClick={goBack} disabled={generating}>
                Cancelar
              </button>
              <button type="submit" className="ecoBtn ecoBtn--primary" disabled={busy}>
                <EcoIcon name="camera" size={18} />
                {generating ? "Gerando..." : "Gerar criativo"}
              </button>
            </div>
            <p className="cmpHint">Cada arte custa cerca de US$ 0,15 a US$ 0,40 (Google e Eco-IA) e entra no Consumo de IA.</p>
          </section>
        </form>
      </main>
    </div>
  );
};

export default CriativoNovoPage;
